/**
 * useConsentArmDelay — a host consent button is inert for a moment after the
 * sheet (or a new step of it) appears.
 *
 * A plugin decides WHEN a host consent sheet opens. Timed against a click the
 * user is already making — a double-click, a button they were about to press
 * in the app underneath — the sheet's primary button can land under the cursor
 * and take a click that was never aimed at it. Half a second is below what
 * anyone notices when they actually read the question, and above the gap
 * between two clicks of a double-click.
 *
 * Pass a `stepKey` that changes whenever what the button MEANS changes (a new
 * sheet, a new step): the delay restarts from each change.
 */
import { useEffect, useState } from 'react';

export const CONSENT_ARM_DELAY_MS = 500;

export function useConsentArmDelay(stepKey: string | number): boolean {
  const [armedFor, setArmedFor] = useState<string | number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setArmedFor(stepKey), CONSENT_ARM_DELAY_MS);
    return () => clearTimeout(timer);
  }, [stepKey]);

  // Compared, not just set: on the render where `stepKey` changes the previous
  // step's `true` must not carry over for a frame.
  return armedFor === stepKey;
}
